import type { Place, SavedPlace } from './types';

/** The server keeps at most 5 saved places (PUT /v1/rider/me replaces the whole list). */
export const MAX_PLACES = 5;
export type PlaceField = 'home' | 'work' | 'place';

export class TooManyPlaces extends Error {
  constructor() {
    super('TOO_MANY_PLACES');
  }
}

const trim = (s: string, n: number) => s.trim().slice(0, n);

/** The saved place to store for a point: Ghar/Kaam keep their label; any other place needs the rider's name for it. */
export function placeFor(field: PlaceField, at: Place, names: { home: string; work: string }, name?: string): SavedPlace {
  const label = field === 'home' ? 'HOME' : field === 'work' ? 'WORK' : 'OTHER';
  const title = field === 'home' ? names.home : field === 'work' ? names.work : trim(name ?? '', 40) || 'Saved place';
  return { label, name: title, address: trim(at.address || title, 300), lat: at.lat, lng: at.lng };
}

/** Add or replace: one HOME and one WORK (a new one replaces the old); OTHER places replace one with the same name. */
export function upsertPlace(list: SavedPlace[], p: SavedPlace): SavedPlace[] {
  const same = (x: SavedPlace) => (p.label === 'OTHER' ? x.label === 'OTHER' && x.name.toLowerCase() === p.name.toLowerCase() : x.label === p.label);
  const rest = list.filter((x) => !same(x));
  if (rest.length >= MAX_PLACES) throw new TooManyPlaces();
  // Ghar, Kaam first, then the others in the order they were added
  return [...rest, p].sort((a, b) => order(a) - order(b));
}

const order = (p: SavedPlace) => (p.label === 'HOME' ? 0 : p.label === 'WORK' ? 1 : 2);

export const removePlace = (list: SavedPlace[], p: SavedPlace) => list.filter((x) => !(x.label === p.label && x.name === p.name && x.lat === p.lat && x.lng === p.lng));

export const toPlace = (p: SavedPlace): Place => ({ lat: p.lat, lng: p.lng, address: p.address });
